// FinanceStaffLeaves.jsx — mirrors admin StaffLeaves.jsx; add/review go through admin-approval requests
import { sortAsc } from '../utils/sortOptions';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon, XCircleIcon, ClockIcon,
  PlusIcon, XMarkIcon, PaperClipIcon, MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { ALL_DESIGNATIONS, NON_PORTAL_COLORS } from '../constants/staffDesignations';
import { useSchoolModules } from '../hooks/useSchoolModules';

const BASE_PORTAL_TABS = [
  { key: '',            label: 'All Staff',    color: '#3b6ef8' },
  { key: 'teacher',     label: 'Teachers',     color: '#3b6ef8' },
  { key: 'coordinator', label: 'Coordinators', color: '#8b5cf6' },
  { key: 'finance',     label: 'Finance',      color: '#0d9488' },
];

const BASE_PORTAL_ROLES = ['teacher','coordinator','finance'];
const LEAVE_TYPES  = ['sick','casual','emergency','maternity','unpaid','annual','other'];
const STATUS_COLOR = { pending:'#f59e0b','approved-paid':'#10c98e','approved-unpaid':'#3b6ef8','approved-half':'#8b5cf6',rejected:'#ef4444' };
const STATUS_BG    = { pending:'#fffbeb','approved-paid':'#ecfdf5','approved-unpaid':'#eef2ff','approved-half':'#f5f3ff',rejected:'#fef2f2' };

const fmtDate  = d => d ? new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'}) : '—';
const initials = n => (n||'').split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sl * { box-sizing:border-box; } .sl { font-family:'Sora',sans-serif; color:#1a1d2e; }
  .sl-title { font-size:1.4rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; margin-bottom:3px; }
  .sl-sub   { font-size:.76rem; color:#9094a8; margin-bottom:1rem; }
  .req-banner { background:#f0fdfa; border:1px solid #99f6e4; border-radius:10px; padding:9px 14px; font-size:.76rem; color:#0f766e; font-weight:600; margin-bottom:1.2rem; }
  .role-tabs { display:flex; gap:5px; flex-wrap:wrap; margin-bottom:1.2rem; }
  .role-tab  { padding:6px 14px; border-radius:99px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.74rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .role-tab.active { color:white; border-color:transparent; }
  .sl-toolbar { display:flex; gap:.7rem; flex-wrap:wrap; align-items:center; margin-bottom:1.2rem; }
  .sl-select { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; outline:none; }
  .sl-search-wrap { position:relative; flex:1; max-width:260px; }
  .sl-search-wrap svg { position:absolute; left:11px; top:50%; transform:translateY(-50%); width:14px; height:14px; stroke:#9094a8; pointer-events:none; }
  .sl-search { font-family:'Sora',sans-serif; font-size:.79rem; width:100%; padding:8px 12px 8px 30px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; outline:none; }
  .btn-add { display:inline-flex; align-items:center; gap:6px; padding:8px 18px; border:none; border-radius:10px; background:#0d9488; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; margin-left:auto; }
  .btn-add svg { width:15px; height:15px; stroke-width:2.5; }
  .sl-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .sl-tbl-wrap { overflow-x:auto; }
  .sl-tbl { width:100%; border-collapse:collapse; }
  .sl-tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .sl-tbl th { padding:10px 14px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .sl-tbl tbody tr { border-bottom:1px solid #f3f5fb; } .sl-tbl tbody tr:last-child { border-bottom:none; } .sl-tbl tbody tr:hover { background:#fafbff; }
  .sl-tbl td { padding:10px 14px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .sl-avatar { width:32px; height:32px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:.7rem; font-weight:700; color:white; flex-shrink:0; }
  .status-pill { font-size:.67rem; font-weight:700; padding:3px 9px; border-radius:99px; display:inline-flex; white-space:nowrap; }
  .type-pill   { font-size:.66rem; font-weight:600; padding:2px 8px; border-radius:99px; background:#f0f2f7; color:#6b7194; }
  .role-pill   { font-size:.66rem; font-weight:600; padding:2px 8px; border-radius:99px; display:inline-flex; white-space:nowrap; }
  .action-btn  { width:27px; height:27px; border-radius:7px; border:1px solid; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
  .action-btn svg { width:12px; height:12px; stroke-width:2; }
  .approve-paid:hover   { background:#10c98e; color:white; border-color:#10c98e; }
  .approve-unpaid:hover { background:#3b6ef8; color:white; border-color:#3b6ef8; }
  .reject-btn:hover { background:#ef4444; color:white; border-color:#ef4444; }
  .sl-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .sl-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .sl-spin { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#0d9488; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  .mb { position:fixed; inset:0; background:rgba(10,12,28,.65); backdrop-filter:blur(5px); display:flex; align-items:flex-start; justify-content:center; z-index:9999; padding:20px; overflow-y:auto; }
  .mo { background:white; border-radius:20px; width:100%; max-width:500px; max-height:92vh; display:flex; flex-direction:column; box-shadow:0 25px 60px rgba(0,0,0,.25); animation:moIn .25s ease; overflow:hidden; margin:auto; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .mo-hd { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; }
  .mo-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .mo-x { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .mo-x svg { width:14px; height:14px; stroke-width:2; }
  .mo-bd { padding:20px 22px; overflow-y:auto; display:flex; flex-direction:column; gap:12px; }
  .mo-ft { display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; flex-shrink:0; }
  .ff { display:flex; flex-direction:column; gap:5px; } .fg { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .form-grid > *, .fg > * { min-width:0; }
  .fl { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fi,.fs,.fta { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%;min-width:0; }
  .fi:focus,.fs:focus,.fta:focus { border-color:#0d9488; background:white; }
  .fta { resize:vertical; min-height:60px; }
  .btn-save  { padding:9px 20px; border:none; border-radius:10px; background:#0d9488; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .btn-save:disabled { opacity:.6; cursor:not-allowed; }
  .btn-ghost { padding:9px 18px; border:none; border-radius:10px; background:#f0f2f7; color:#6b7194; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .btn-green { padding:9px 20px; border:none; border-radius:10px; background:#10c98e; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .btn-red   { padding:9px 20px; border:none; border-radius:10px; background:#ef4444; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .btn-purple{ padding:9px 20px; border:none; border-radius:10px; background:#8b5cf6; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .btn-blue2 { padding:9px 20px; border:none; border-radius:10px; background:#3b6ef8; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .leave-detail-row { display:flex; justify-content:space-between; font-size:.8rem; padding:5px 0; border-bottom:1px solid #f3f5fb; }
  .leave-detail-row:last-child { border-bottom:none; }
  .leave-detail-label { color:#9094a8; font-weight:600; }
  .leave-detail-val { color:#0f1123; font-weight:600; text-align:right; }
  .sl-cards { display:none; padding:10px; }
  .sl-card  { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .sl-card-top { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .sl-card-row { display:flex; justify-content:space-between; align-items:flex-start; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .sl-card-row:last-of-type { border-bottom:none; }
  .sl-card-label { color:#9094a8; font-weight:600; white-space:nowrap; flex-shrink:0; }
  .sl-card-actions { display:flex; gap:6px; flex-wrap:wrap; margin-top:8px; }
  @media (max-width:768px) {
    .sl-tbl-wrap { display:none !important; }
    .sl-cards { display:block; }
  }
`;

const PolicyBadge = ({ leaveType }) => {
  const [policy, setPolicy] = React.useState(null);
  React.useEffect(() => {
    api.get('/finance/holidays/settings').then(r => setPolicy(r.data?.leavePolicy?.[leaveType]||'unpaid')).catch(()=>{});
  }, [leaveType]);
  if (!policy) return null;
  const color = policy==='paid'?'#10c98e':policy==='half'?'#8b5cf6':'#ef4444';
  const bg    = policy==='paid'?'#ecfdf5':policy==='half'?'#f5f3ff':'#fef2f2';
  return <span style={{fontSize:'.66rem',fontWeight:700,padding:'2px 8px',borderRadius:99,background:bg,color}}>{policy==='paid'?'Paid':policy==='half'?'Half-paid':'Unpaid'}</span>;
};

export default function FinanceStaffLeaves() {
  const { libraryEnabled, hostelEnabled } = useSchoolModules();
  const PORTAL_TABS = [
    ...BASE_PORTAL_TABS,
    ...(libraryEnabled ? [{ key: 'librarian',     label: 'Librarians',   color: '#f59e0b' }] : []),
    ...(hostelEnabled  ? [{ key: 'hostel_member', label: 'Hostel Staff', color: '#ec4899' }] : []),
  ];
  const PORTAL_ROLES = [...BASE_PORTAL_ROLES, ...(libraryEnabled ? ['librarian'] : []), ...(hostelEnabled ? ['hostel_member'] : [])];
  const [activeRole, setActiveRole] = useState('');
  const [statusF,    setStatusF]    = useState('');
  const [monthF,     setMonthF]     = useState('');
  const [search,     setSearch]     = useState('');
  const [leaves,     setLeaves]     = useState([]);
  const [portalStaff,setPortalStaff]= useState([]);
  const [nonPortal,  setNonPortal]  = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [submitting, setSubmitting] = useState({});
  const [addModal,   setAddModal]   = useState(false);
  const [reviewModal,setReviewModal]= useState(null); // leave object being reviewed
  const [adminNote,  setAdminNote]  = useState('');
  const [reviewStatus, setReviewStatus] = useState('');
  const [saving,     setSaving]     = useState(false);
  const [docFile,    setDocFile]    = useState(null);
  const [docName,    setDocName]    = useState('');
  const [uploading,  setUploading]  = useState(false);
  const [quota,      setQuota]      = useState(null);
  const [myUserId,   setMyUserId]   = useState(null);
  const [form, setForm] = useState({
    isPortalStaff:true, userId:'', staffId:'', applicantRole:'teacher',
    type:'sick', startDate:'', endDate:'', reason:'', documentUrl:'',
  });

  useEffect(() => {
    api.get('/finance/profile').then(r => setMyUserId(String(r.data?._id||''))).catch(()=>{});
  }, []);

  const loadLeaves = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeRole) params.append('role', activeRole);
      if (statusF)    params.append('status', statusF);
      if (monthF)     params.append('month', monthF);
      const { data } = await api.get(`/finance/staff-leaves?${params}`);
      setLeaves(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const loadStaff = async () => {
    try {
      const roles = ['teacher','coordinator','finance'];
      const all = [];
      for (const r of roles) {
        const { data } = await api.get(`/finance/salary/staff?role=${r}`);
        (Array.isArray(data)?data:[]).forEach(s => all.push({...s,role:r}));
      }
      setPortalStaff(all);
    } catch (err) { console.error('Portal staff load error:', err); }
    try {
      const { data: np } = await api.get('/finance/salary/non-portal-staff');
      const active = (Array.isArray(np)?np:[]).filter(s => s.is_active !== false && s.isActive !== false);
      setNonPortal(active);
    } catch (err) { console.error('Non-portal staff load error:', err); }
  };

  useEffect(() => { loadLeaves(); }, [activeRole, statusF, monthF]);
  useEffect(() => { loadStaff(); }, []);

  // Open review modal
  const openReview = (leave, status) => {
    setReviewModal(leave);
    setReviewStatus(status);
    setAdminNote('');
  };

  // Submit review as a request
  const handleReviewSubmit = async () => {
    if (!reviewModal) return;
    if (reviewStatus === 'rejected' && !adminNote.trim()) { toast.error('Reason is required to reject'); return; }
    setSubmitting(p => ({...p,[reviewModal._id]:reviewStatus}));
    try {
      await api.post('/finance/requests', {
        type: 'staff_leave_review',
        payload: {
          leaveId: reviewModal._id,
          staffName: reviewModal.user?.name || reviewModal.staff?.name || '—',
          status: reviewStatus,
          adminNote: adminNote || undefined,
        },
        note: `${reviewStatus.replace(/-/g,' ')} — ${reviewModal.user?.name || reviewModal.staff?.name || ''}`,
      });
      toast.success('Review request submitted — awaiting admin approval');
      setReviewModal(null);
      setAdminNote('');
    } catch { toast.error('Failed to submit request'); }
    finally { setSubmitting(p => ({...p,[reviewModal?._id]:null})); }
  };

  const handleDocUpload = async (file) => {
    if (!file) return null;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/finance/staff-leaves/upload-doc', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.url;
    } catch {
      toast.error('Failed to upload document');
      return null;
    } finally { setUploading(false); }
  };

  const handleAdd = async () => {
    if (!form.startDate||!form.endDate) return toast.error('Dates are required');
    if (form.isPortalStaff&&!form.userId) return toast.error('Select staff member');
    if (!form.isPortalStaff&&!form.staffId) return toast.error('Select staff member');
    if (!form.reason.trim()) return toast.error('Reason is required');
    setSaving(true);
    try {
      let docUrl = form.documentUrl;
      if (docFile) {
        docUrl = await handleDocUpload(docFile);
        if (!docUrl) { setSaving(false); return; }
      }
      const staffName = form.isPortalStaff
        ? portalStaff.find(s => s._id === form.userId)?.name
        : nonPortal.find(s => s._id === form.staffId)?.name;
      await api.post('/finance/requests', {
        type: 'staff_leave_add',
        payload: { ...form, staffName, documentUrl: docUrl, isPortalStaff: form.isPortalStaff===true||form.isPortalStaff==='true' },
        note: `Add leave — ${staffName || ''} (${form.type})`,
      });
      toast.success('Add leave request submitted — awaiting admin approval');
      setAddModal(false); setQuota(null); setDocFile(null); setDocName('');
      setForm({isPortalStaff:true,userId:'',staffId:'',applicantRole:'teacher',type:'sick',startDate:'',endDate:'',reason:'',documentUrl:''});
    } catch (err) {
      const d = err.response?.data;
      if (d?.offDayBlocked) toast.error(`Cannot apply leave on off-days:\n${(d.invalidDates||[]).map(x=>`${x.date} (${x.reason})`).join('\n')}`, {duration:6000});
      else if (d?.quotaExceeded) toast.error(d.message, {duration:5000});
      else toast.error(d?.message||'Failed to submit request');
    }
    finally { setSaving(false); }
  };

  const isOwnLeave = (l) => myUserId && String(l.user?._id||l.user||'') === String(myUserId);
  const getRoleTab = (key) => PORTAL_TABS.find(t => t.key === key) || { key, label: key, color: NON_PORTAL_COLORS[ALL_DESIGNATIONS.indexOf(key) % NON_PORTAL_COLORS.length] || '#f59e0b' };
  const filtered   = leaves.filter(l => !search || (l.user?.name||l.staff?.name||'').toLowerCase().includes(search.toLowerCase()));
  const isPortalForm = form.isPortalStaff===true||form.isPortalStaff==='true';

  // Review modal button label and color
  const reviewBtnStyle = reviewStatus==='approved-paid'   ? 'btn-green'
                       : reviewStatus==='approved-half'   ? 'btn-purple'
                       : reviewStatus==='approved-unpaid' ? 'btn-blue2'
                       : 'btn-red';
  const reviewBtnLabel = reviewStatus==='approved-paid'   ? '✓ Approve (Full Pay)'
                       : reviewStatus==='approved-half'   ? '✓ Approve (Half Pay)'
                       : reviewStatus==='approved-unpaid' ? '✓ Approve (Unpaid)'
                       : '✗ Reject';

  return (
    <div className="sl"><style>{css}</style>
      <div className="sl-title">Staff Leaves</div>
      <div className="sl-sub">View leave applications for all staff roles.</div>
      <div className="req-banner">📋 Adding and reviewing leaves are submitted as requests — admin must approve before they take effect.</div>

      <div className="role-tabs">
        {[PORTAL_TABS[0], ...sortAsc([...PORTAL_TABS.slice(1), ...ALL_DESIGNATIONS.map((d, i) => ({ key: d, label: d, color: NON_PORTAL_COLORS[i % NON_PORTAL_COLORS.length] }))], 'label')].map(t => (
          <button key={t.key} className={`role-tab ${activeRole===t.key?'active':''}`}
            style={activeRole===t.key ? { background:t.color } : {}}
            onClick={() => setActiveRole(t.key)}>{t.label}</button>
        ))}
      </div>

      <div className="sl-toolbar">
        <div className="sl-search-wrap"><MagnifyingGlassIcon /><input className="sl-search" placeholder="Search by name…" value={search} onChange={e=>setSearch(e.target.value)} /></div>
        <select className="sl-select" value={statusF} onChange={e=>setStatusF(e.target.value)}>
          <option value="">All Status</option>
          <option value="approved-paid">Approved (Paid)</option>
          <option value="approved-unpaid">Approved (Unpaid)</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
        </select>
        <input type="month" className="sl-select" value={monthF} onChange={e=>setMonthF(e.target.value)} />
        <button className="btn-add" onClick={()=>setAddModal(true)}><PlusIcon />Request Add Leave</button>
      </div>

      <div className="sl-panel">
        {loading ? <div className="sl-loading"><div className="sl-spin"/>Loading…</div>
        : filtered.length===0 ? <div className="sl-empty">No leave applications found.</div>
        : (
          <div className="sl-tbl-wrap">
            <table className="sl-tbl">
              <thead><tr>
                <th>Staff</th><th>Role</th><th>Type</th><th>From</th><th>To</th>
                <th>Reason</th><th>Doc</th><th>Policy</th><th>Status</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map(l => {
                  const name    = l.user?.name || l.staff?.name || '—';
                  const role    = l.applicant_role || l.applicantRole || '—';
                  const roleTab = getRoleTab(role);
                  const own     = isOwnLeave(l);
                  return (
                    <tr key={l._id} style={own ? {background:'#fafbff'} : {}}>
                      <td>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <div className="sl-avatar" style={{background:`linear-gradient(135deg,${roleTab?.color||'#9094a8'},${roleTab?.color||'#9094a8'}99)`}}>{initials(name)}</div>
                          <div>
                            <div style={{fontWeight:600,color:'#0f1123'}}>{name}</div>
                            {own && <div style={{fontSize:'.64rem',color:'#0d9488',fontWeight:600}}>👤 You — use My Leaves</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="role-pill" style={{background:`${roleTab?.color||'#9094a8'}18`,color:roleTab?.color||'#9094a8'}}>
                          {role}
                        </span>
                      </td>
                      <td><span className="type-pill">{l.type}</span></td>
                      <td style={{fontSize:'.76rem',color:'#6b7194',whiteSpace:'nowrap'}}>{fmtDate(l.start_date||l.startDate)}</td>
                      <td style={{fontSize:'.76rem',color:'#6b7194',whiteSpace:'nowrap'}}>{fmtDate(l.end_date||l.endDate)}</td>
                      <td style={{fontSize:'.74rem',color:'#6b7194',maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{l.reason||'—'}</td>
                      <td>
                        {l.document_url||l.documentUrl
                          ? <a href={l.document_url||l.documentUrl} target="_blank" rel="noreferrer" style={{color:'#0d9488',display:'flex',alignItems:'center',gap:3,fontSize:'.74rem'}}><PaperClipIcon style={{width:13,height:13}}/>View</a>
                          : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}
                      </td>
                      <td><PolicyBadge leaveType={l.type} /></td>
                      <td>
                        <span className="status-pill" style={{background:STATUS_BG[l.status],color:STATUS_COLOR[l.status]}}>
                          {l.status?.replace(/-/g,' ')}
                        </span>
                      </td>
                      <td>
                        {own ? (
                          <span style={{fontSize:'.72rem',color:'#9094a8',fontStyle:'italic'}}>Own leave</span>
                        ) : l.status==='pending' ? (
                          <div style={{display:'flex',gap:4}}>
                            <button className="action-btn approve-paid"   style={{color:'#10c98e',borderColor:'#a7f3d0',background:'#ecfdf5'}} disabled={submitting[l._id]} onClick={()=>openReview(l,'approved-paid')}   title="Request Approve (Paid)"><CheckCircleIcon /></button>
                            <button className="action-btn"                style={{color:'#8b5cf6',borderColor:'#ddd6fe',background:'#f5f3ff'}} disabled={submitting[l._id]} onClick={()=>openReview(l,'approved-half')}   title="Request Approve (Half-paid)">½</button>
                            <button className="action-btn approve-unpaid" style={{color:'#3b6ef8',borderColor:'#c7d2fe',background:'#eef2ff'}} disabled={submitting[l._id]} onClick={()=>openReview(l,'approved-unpaid')} title="Request Approve (Unpaid)"><ClockIcon /></button>
                            <button className="action-btn reject-btn"     style={{color:'#ef4444',borderColor:'#fecaca',background:'#fef2f2'}} disabled={submitting[l._id]} onClick={()=>openReview(l,'rejected')}         title="Request Reject"><XCircleIcon /></button>
                          </div>
                        ) : (
                          <div>
                            <div style={{fontSize:'.72rem',color:'#9094a8'}}>{l.reviewed_by||l.reviewedBy||'—'}</div>
                            {(l.admin_note||l.adminNote) && <div style={{fontSize:'.68rem',color:'#6b7194',marginTop:2,fontStyle:'italic'}}>"{l.admin_note||l.adminNote}"</div>}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="sl-cards">
            {filtered.map(l => {
              const name    = l.user?.name || l.staff?.name || '—';
              const role    = l.applicant_role || l.applicantRole || '—';
              const roleTab = getRoleTab(role);
              const own     = isOwnLeave(l);
              return (
                <div key={l._id} className="sl-card" style={own ? { background:'#f0fdfa' } : {}}>
                  <div className="sl-card-top">
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <div className="sl-avatar" style={{background:`linear-gradient(135deg,${roleTab?.color||'#9094a8'},${roleTab?.color||'#9094a8'}99)`}}>{initials(name)}</div>
                      <div>
                        <div style={{fontWeight:600,color:'#0f1123'}}>{name}</div>
                        <span className="role-pill" style={{background:`${roleTab?.color||'#9094a8'}18`,color:roleTab?.color||'#9094a8'}}>{role}</span>
                        {own && <div style={{fontSize:'.64rem',color:'#0d9488',fontWeight:600,marginTop:2}}>👤 You — use My Leaves</div>}
                      </div>
                    </div>
                    <span className="status-pill" style={{background:STATUS_BG[l.status],color:STATUS_COLOR[l.status]}}>{l.status?.replace(/-/g,' ')}</span>
                  </div>
                  <div className="sl-card-row"><span className="sl-card-label">Type</span><span className="type-pill">{l.type}</span></div>
                  <div className="sl-card-row"><span className="sl-card-label">From</span><span>{fmtDate(l.start_date||l.startDate)}</span></div>
                  <div className="sl-card-row"><span className="sl-card-label">To</span><span>{fmtDate(l.end_date||l.endDate)}</span></div>
                  {l.reason && <div className="sl-card-row"><span className="sl-card-label">Reason</span><span style={{textAlign:'right',color:'#6b7194'}}>{l.reason}</span></div>}
                  <div className="sl-card-row">
                    <span className="sl-card-label">Doc</span>
                    {l.document_url||l.documentUrl
                      ? <a href={l.document_url||l.documentUrl} target="_blank" rel="noreferrer" style={{color:'#0d9488',display:'flex',alignItems:'center',gap:3,fontSize:'.74rem'}}><PaperClipIcon style={{width:13,height:13}}/>View</a>
                      : <span style={{color:'#c0c5dc'}}>—</span>}
                  </div>
                  <div className="sl-card-row"><span className="sl-card-label">Policy</span><PolicyBadge leaveType={l.type} /></div>
                  {!own && l.status === 'pending' && (
                    <div className="sl-card-actions">
                      <button className="action-btn approve-paid"   style={{color:'#10c98e',borderColor:'#a7f3d0',background:'#ecfdf5'}} disabled={submitting[l._id]} onClick={()=>openReview(l,'approved-paid')}   title="Request Approve (Paid)"><CheckCircleIcon /></button>
                      <button className="action-btn"                style={{color:'#8b5cf6',borderColor:'#ddd6fe',background:'#f5f3ff'}} disabled={submitting[l._id]} onClick={()=>openReview(l,'approved-half')}   title="Request Approve (Half-paid)">½</button>
                      <button className="action-btn approve-unpaid" style={{color:'#3b6ef8',borderColor:'#c7d2fe',background:'#eef2ff'}} disabled={submitting[l._id]} onClick={()=>openReview(l,'approved-unpaid')} title="Request Approve (Unpaid)"><ClockIcon /></button>
                      <button className="action-btn reject-btn"     style={{color:'#ef4444',borderColor:'#fecaca',background:'#fef2f2'}} disabled={submitting[l._id]} onClick={()=>openReview(l,'rejected')}         title="Request Reject"><XCircleIcon /></button>
                    </div>
                  )}
                  {!own && l.status !== 'pending' && (
                    <div className="sl-card-row">
                      <span className="sl-card-label">Reviewed By</span>
                      <span style={{textAlign:'right'}}>
                        <div style={{fontSize:'.72rem',color:'#9094a8'}}>{l.reviewed_by||l.reviewedBy||'—'}</div>
                        {(l.admin_note||l.adminNote) && <div style={{fontSize:'.68rem',color:'#6b7194',marginTop:2,fontStyle:'italic'}}>"{l.admin_note||l.adminNote}"</div>}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Review Modal ── */}
      {reviewModal && createPortal(
        <div className="mb" onClick={()=>setReviewModal(null)}>
          <div className="mo" onClick={e=>e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">Request: Review Leave</div>
              <button className="mo-x" onClick={()=>setReviewModal(null)}><XMarkIcon /></button>
            </div>
            <div className="mo-bd">
              <div style={{background:'#f8f9fc',borderRadius:10,padding:'12px 14px'}}>
                <div style={{fontWeight:700,fontSize:'.84rem',color:'#0f1123',marginBottom:8}}>
                  {reviewModal.user?.name||reviewModal.staff?.name||'—'}
                </div>
                <div className="leave-detail-row">
                  <span className="leave-detail-label">Type</span>
                  <span className="leave-detail-val" style={{textTransform:'capitalize'}}>{reviewModal.type}</span>
                </div>
                <div className="leave-detail-row">
                  <span className="leave-detail-label">From</span>
                  <span className="leave-detail-val">{fmtDate(reviewModal.start_date||reviewModal.startDate)}</span>
                </div>
                <div className="leave-detail-row">
                  <span className="leave-detail-label">To</span>
                  <span className="leave-detail-val">{fmtDate(reviewModal.end_date||reviewModal.endDate)}</span>
                </div>
                {reviewModal.reason && (
                  <div className="leave-detail-row">
                    <span className="leave-detail-label">Reason</span>
                    <span className="leave-detail-val" style={{maxWidth:200,textAlign:'right'}}>{reviewModal.reason}</span>
                  </div>
                )}
              </div>

              <div style={{
                padding:'10px 14px', borderRadius:10, fontWeight:700, fontSize:'.82rem',
                background: reviewStatus==='approved-paid'   ? '#ecfdf5'
                           : reviewStatus==='approved-half'  ? '#f5f3ff'
                           : reviewStatus==='approved-unpaid'? '#eef2ff'
                           : '#fef2f2',
                color: reviewStatus==='approved-paid'   ? '#10c98e'
                      : reviewStatus==='approved-half'  ? '#8b5cf6'
                      : reviewStatus==='approved-unpaid'? '#3b6ef8'
                      : '#ef4444',
              }}>
                Decision: {reviewStatus==='approved-paid'   ? '✓ Approve with Full Pay'
                          : reviewStatus==='approved-half'  ? '✓ Approve with Half Pay'
                          : reviewStatus==='approved-unpaid'? '✓ Approve — Unpaid'
                          : '✗ Reject Leave'} <span style={{fontWeight:400}}>(pending admin approval)</span>
              </div>

              <div className="ff">
                <label className="fl">{reviewStatus==='rejected' ? 'Reason for Rejection' : 'Note to Staff (Optional)'}</label>
                <textarea
                  className="fta"
                  required={reviewStatus==='rejected'}
                  value={adminNote}
                  onChange={e=>setAdminNote(e.target.value)}
                  placeholder="Add a note for the staff member… e.g. 'Get well soon' or reason for rejection"
                />
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn-ghost" onClick={()=>setReviewModal(null)}>Cancel</button>
              <button
                className={reviewBtnStyle}
                onClick={handleReviewSubmit}
                disabled={submitting[reviewModal._id]}
              >
                {submitting[reviewModal._id] ? 'Submitting…' : `Submit Request: ${reviewBtnLabel}`}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Add Leave Modal ── */}
      {addModal && createPortal(
        <div className="mb" onClick={()=>setAddModal(false)}>
          <div className="mo" onClick={e=>e.stopPropagation()}>
            <div className="mo-hd"><div className="mo-title">Request: Add Leave Application</div><button className="mo-x" onClick={()=>setAddModal(false)}><XMarkIcon /></button></div>
            <div className="mo-bd">
              <div style={{display:'flex',gap:8,marginBottom:4}}>
                <button onClick={()=>setForm(p=>({...p,isPortalStaff:true,staffId:''}))} style={{flex:1,padding:'8px',border:`1.5px solid ${form.isPortalStaff===true?'#0d9488':'#e0e4f0'}`,borderRadius:9,background:form.isPortalStaff===true?'#f0fdfa':'white',color:form.isPortalStaff===true?'#0d9488':'#6b7194',fontFamily:'Sora',fontSize:'.8rem',fontWeight:600,cursor:'pointer'}}>Portal Staff</button>
                <button onClick={()=>setForm(p=>({...p,isPortalStaff:false,userId:''}))} style={{flex:1,padding:'8px',border:`1.5px solid ${form.isPortalStaff===false?'#f59e0b':'#e0e4f0'}`,borderRadius:9,background:form.isPortalStaff===false?'#fffbeb':'white',color:form.isPortalStaff===false?'#92400e':'#6b7194',fontFamily:'Sora',fontSize:'.8rem',fontWeight:600,cursor:'pointer'}}>Non-Portal Staff</button>
              </div>
              {isPortalForm ? (
                <div className="ff"><label className="fl">Staff Member *</label>
                  <select className="fs" value={form.userId} onChange={e=>{const s=portalStaff.find(x=>x._id===e.target.value);setForm(p=>({...p,userId:e.target.value,applicantRole:s?.role||'teacher'}));}}>
                    <option value="">Select staff…</option>
                    {portalStaff.map(s=><option key={s._id} value={s._id}>[{s.role}] {s.name}</option>)}
                  </select>
                </div>
              ) : (
                <div className="ff"><label className="fl">Staff Member *</label>
                  <select className="fs" value={form.staffId} onChange={e=>{const s=nonPortal.find(x=>x._id===e.target.value);setForm(p=>({...p,staffId:e.target.value,applicantRole:s?.designation||'Peon'}));if(e.target.value)api.get(`/finance/staff-leaves/quota?staffId=${e.target.value}&isPortalStaff=false`).then(r=>setQuota(r.data?.stats||null)).catch(()=>setQuota(null));}}>
                    <option value="">Select staff…</option>
                    {nonPortal.map(s=><option key={s._id} value={s._id}>[{s.designation}] {s.name}</option>)}
                  </select>
                </div>
              )}
              <div className="ff"><label className="fl">Leave Type *</label>
                <select className="fs" value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))}>
                  {LEAVE_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              {quota&&form.type&&(()=>{const q=quota.find(x=>x.type===form.type);if(!q)return null;const isUn=q.quota==='unlimited';const pct=isUn?0:Math.round((q.used/q.quota)*100);const color=isUn?'#9094a8':pct>=100?'#ef4444':pct>=80?'#f59e0b':'#10c98e';return(<div style={{background:'#f8f9fc',borderRadius:10,padding:'10px 12px',border:`1.5px solid ${color}30`}}><div style={{display:'flex',justifyContent:'space-between',fontSize:'.76rem',fontWeight:700,color:'#0f1123',marginBottom:6}}><span style={{textTransform:'capitalize'}}>{form.type} Leave</span><span style={{color}}>{isUn?'Unlimited':`${q.remaining} remaining`}</span></div>{!isUn&&<div style={{height:6,background:'#e0e4f0',borderRadius:99,overflow:'hidden'}}><div style={{height:'100%',width:`${Math.min(100,pct)}%`,background:color,borderRadius:99}}/></div>}<div style={{fontSize:'.68rem',color:'#9094a8',marginTop:5}}>{!isUn&&<>Used: {q.used}/{q.quota}</>}{pct>=100&&<span style={{color:'#ef4444',fontWeight:700}}> · QUOTA FULL</span>}</div></div>);})()}
              <div className="fg">
                <div className="ff"><label className="fl">Start Date *</label><input type="date" className="fi" value={form.startDate} onChange={e=>setForm(p=>({...p,startDate:e.target.value}))}/></div>
                <div className="ff"><label className="fl">End Date *</label><input type="date" className="fi" value={form.endDate} onChange={e=>setForm(p=>({...p,endDate:e.target.value}))}/></div>
              </div>
              <div className="ff"><label className="fl">Reason</label><textarea className="fta" required value={form.reason} onChange={e=>setForm(p=>({...p,reason:e.target.value}))} placeholder="Leave reason…"/></div>
              {!isPortalForm&&(
                <div className="ff">
                  <label className="fl">Document (PDF, DOCX, TXT, Image)</label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                    className="fi"
                    style={{padding:'6px 11px',cursor:'pointer'}}
                    onChange={e => {
                      const file = e.target.files[0];
                      if (file) { setDocFile(file); setDocName(file.name); }
                      else { setDocFile(null); setDocName(''); }
                    }}
                  />
                  {docName && <span style={{fontSize:'.7rem',color:'#10c98e',marginTop:2}}>✓ {docName}</span>}
                  {uploading && <span style={{fontSize:'.7rem',color:'#9094a8',marginTop:2}}>Uploading…</span>}
                </div>
              )}
            </div>
            <div className="mo-ft">
              <button className="btn-ghost" onClick={()=>setAddModal(false)}>Cancel</button>
              <button className="btn-save" onClick={handleAdd} disabled={saving||uploading}>{saving?'Submitting…':uploading?'Uploading…':'Submit Request'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
}
